/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useState, useCallback, useMemo, useRef } from '@web-stories-wp/react';
import { EditorState, Modifier } from 'draft-js';

/**
 * Internal dependencies
 */
import {
  getEmptyLineHeightAtCaret,
  getExplicitEmptyLineHeight,
  preserveDeletedLineHeight,
} from './emptyLineHeight';
import { lineHeightToStyle } from './formatters/lineHeight';
import { LINE_HEIGHT } from './customConstants';
import { useCanvas } from '../../app';
import RichTextContext from './context';
import {
  normalizeTextFontReferences,
  getTextFontReferenceMap,
  hasUnsupportedTextFontReferences,
  hasUnresolvedTextFontReferences,
  ensureTextFontReference,
  pruneUnusedFontReferences,
} from '../../elements/text/utils/mixedTypography';
import {
  getSelectionForAll,
  getSelectionForOffset,
  getHandleKeyCommandFromState,
} from './util';
import getStateInfo from './getStateInfo';
import { useFauxSelection } from './fauxSelection';
import customImport from './customImport';
import customExport from './customExport';
import useHandlePastedText from './useHandlePastedText';
import useSelectionManipulation from './useSelectionManipulation';
import fontFamilyFormatter from './formatters/fontFamily';
import fontSizeFormatter from './formatters/fontSize';
import lineHeightFormatter from './formatters/lineHeight';
import weightFormatter from './formatters/weight';
import italicFormatter from './formatters/italic';
import underlineFormatter from './formatters/underline';
import colorFormatter from './formatters/color';
import letterSpacingFormatter from './formatters/letterSpacing';
import {
  decodeMixedTypographyClipboard,
  encodeMixedTypographyClipboard,
  getMixedTypographyMimeDataFromHtml,
  isMixedTypographyClipboardFromCurrentSession,
  MIXED_TYPOGRAPHY_CLIPBOARD_MIME_TYPE,
} from './clipboard';

function getClipboardData(event, type) {
  try {
    return event?.clipboardData?.getData?.(type) || '';
  } catch {
    return '';
  }
}

function writeClipboardData(event, payload) {
  const clipboardData = event?.clipboardData;
  if (!clipboardData?.setData) {
    return false;
  }

  try {
    clipboardData.setData('text/plain', payload.plainText);
    clipboardData.setData('text/html', payload.html);
  } catch {
    return false;
  }

  // Some browsers reject custom MIME types. Standard HTML and text still
  // provide a safe, interoperable fallback in that case.
  try {
    clipboardData.setData(payload.mimeType, payload.mimeData);
  } catch {
    // Continue with the standard clipboard representations written above.
  }
  event.preventDefault();
  return true;
}

function areFontsEqual(left, right) {
  if (!left || !right) {
    return false;
  }

  return (
    left.family === right.family &&
    left.service === right.service &&
    left.sourceId === right.sourceId &&
    left.url === right.url &&
    JSON.stringify(left.urls || []) === JSON.stringify(right.urls || [])
  );
}

function getReferencesFromMap(referenceMap) {
  return normalizeTextFontReferences({
    version: 1,
    items: [...referenceMap.values()],
  });
}

function isWholeContentSelected(editorState) {
  if (!editorState || editorState.getSelection().isCollapsed()) {
    return false;
  }

  const selection = editorState.getSelection();
  const wholeSelection = getSelectionForAll(editorState.getCurrentContent());
  return (
    selection.getStartKey() === wholeSelection.getStartKey() &&
    selection.getStartOffset() === wholeSelection.getStartOffset() &&
    selection.getEndKey() === wholeSelection.getEndKey() &&
    selection.getEndOffset() === wholeSelection.getEndOffset()
  );
}

function RichTextProvider({ children }) {
  const { editingElementState } = useCanvas((state) => ({
    editingElementState: state.state.editingElementState,
  }));
  const [editorState, setEditorStateValue] = useState(null);
  const editorStateRef = useRef(null);
  const sessionKeyRef = useRef(null);
  const referenceRegistryRef = useRef(new Map());
  const opaqueFontReferencesRef = useRef(undefined);
  const formatterContextRef = useRef({});
  const [formatterContext, setFormatterContext] = useState({});
  const [referenceRegistryVersion, setReferenceRegistryVersion] = useState(0);
  const lastKnownStyle = useRef(null);

  const setEditorState = useCallback((stateOrUpdater) => {
    setEditorStateValue((currentState) => {
      let nextState =
        typeof stateOrUpdater === 'function'
          ? stateOrUpdater(currentState)
          : stateOrUpdater;
      nextState = preserveDeletedLineHeight(currentState, nextState);
      // Focus restoration can clear Draft's pending style override. Restore the
      // explicit or inherited custom blank height at every state entry point,
      // including faux selections, so typing agrees with the visible blank.
      if (nextState?.getSelection().isCollapsed()) {
        const block = nextState
          .getCurrentContent()
          .getBlockForKey(nextState.getSelection().getStartKey());
        // A null fallback leaves box-default-only blanks on Draft's existing
        // typing path instead of authoring an unnecessary inline override.
        const height =
          block.getLength() === 0
            ? getEmptyLineHeightAtCaret(nextState, null)
            : null;
        if (height !== null) {
          const styles = nextState
            .getCurrentInlineStyle()
            .filter((style) => !style.startsWith(`${LINE_HEIGHT}-`))
            .add(lineHeightToStyle(height));
          nextState = EditorState.setInlineStyleOverride(nextState, styles);
        }
      }
      editorStateRef.current = nextState;
      return nextState;
    });
  }, []);

  const resolvedFormatterContext = useMemo(
    () => ({
      ...formatterContext,
      fontReferenceMap: referenceRegistryRef.current,
      referenceRegistryVersion,
    }),
    [formatterContext, referenceRegistryVersion]
  );

  const selectionInfo = useMemo(() => {
    if (editorState) {
      return getStateInfo(editorState, resolvedFormatterContext);
    }
    return {
      isBold: false,
      isItalic: false,
      isUnderline: false,
      isUppercase: false,
      hasList: false,
      isListMixed: false,
      isUnorderedList: false,
      isOrderedList: false,
      listDepth: 0,
    };
  }, [editorState, resolvedFormatterContext]);

  const setStateFromValue = useCallback(
    ({
      sessionKey,
      content,
      fontReferences,
      defaultFont,
      defaultFontSize,
      defaultLineHeight,
      defaultFontWeight,
      defaultFontStyle,
    }) => {
      if (
        sessionKeyRef.current === sessionKey &&
        editorStateRef.current !== null
      ) {
        return;
      }

      opaqueFontReferencesRef.current = hasUnsupportedTextFontReferences(
        fontReferences
      )
        ? fontReferences
        : undefined;
      const normalizedReferences = normalizeTextFontReferences(fontReferences);
      referenceRegistryRef.current =
        getTextFontReferenceMap(normalizedReferences);
      const nextFormatterContext = {
        defaultFont,
        defaultFontSize,
        defaultLineHeight,
        defaultFontWeight: Number.isFinite(defaultFontWeight)
          ? defaultFontWeight
          : 400,
        defaultFontStyle: defaultFontStyle === 'italic' ? 'italic' : 'normal',
      };
      formatterContextRef.current = nextFormatterContext;
      sessionKeyRef.current = sessionKey;

      const { offset, clearContent, selectAll } = editingElementState || {};
      let state = EditorState.createWithContent(
        customImport(content, {
          ...nextFormatterContext,
          fontReferenceMap: referenceRegistryRef.current,
        })
      );
      if (clearContent) {
        // If `clearContent` is specified, push the update to clear content so that
        // it can be undone.
        state = EditorState.push(
          state,
          customImport('', nextFormatterContext),
          'remove-range'
        );
      }
      let selection;
      if (selectAll) {
        selection = getSelectionForAll(state.getCurrentContent());
      } else if (!isNaN(offset)) {
        selection = getSelectionForOffset(state.getCurrentContent(), offset);
      }
      if (selection) {
        state = EditorState.forceSelection(state, selection);
      }
      lastKnownStyle.current = state.getCurrentInlineStyle();
      setFormatterContext(nextFormatterContext);
      setReferenceRegistryVersion((version) => version + 1);
      setEditorState(state);
    },
    [editingElementState, setEditorState]
  );

  useFauxSelection(editorState, setEditorState);

  // Paste handlers normalize incoming fragments before insertion. Selection and
  // layout updates must never re-filter the existing document.
  const updateEditorState = useCallback(
    (newEditorState) => {
      let nextState = newEditorState;
      const selection = newEditorState.getSelection();
      const block = newEditorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey());
      const emptyLineHeight = getExplicitEmptyLineHeight(block);
      const isEmpty = nextState.getCurrentContent().getPlainText('') === '';
      if (isEmpty && emptyLineHeight === null) {
        // Copy last known current style as inline style
        nextState = EditorState.setInlineStyleOverride(
          nextState,
          lastKnownStyle.current
        );
      } else {
        lastKnownStyle.current = nextState.getCurrentInlineStyle();
      }
      setEditorState(nextState);
    },
    [setEditorState]
  );

  const getHandleKeyCommand = useCallback(
    () =>
      getHandleKeyCommandFromState(
        updateEditorState,
        formatterContextRef.current
      ),
    [updateEditorState]
  );

  const handlePastedText = useHandlePastedText(setEditorState);

  const getClipboardPayload = useCallback(
    () =>
      encodeMixedTypographyClipboard(editorStateRef.current, {
        ...formatterContextRef.current,
        fontReferenceMap: referenceRegistryRef.current,
      }),
    []
  );

  const hasUnresolvedOpaqueFontReferences = useCallback((state) => {
    if (!opaqueFontReferencesRef.current || !state) {
      return false;
    }

    const content = customExport(state, {
      ...formatterContextRef.current,
      fontReferenceMap: referenceRegistryRef.current,
    });
    return hasUnresolvedTextFontReferences(
      content,
      getReferencesFromMap(referenceRegistryRef.current)
    );
  }, []);

  const handleClipboardCopy = useCallback(
    (_draftEditor, event) => {
      const payload = getClipboardPayload();
      if (!payload) {
        event.preventDefault();
        return;
      }
      writeClipboardData(event, payload);
    },
    [getClipboardPayload]
  );

  const handleClipboardCut = useCallback(
    (_draftEditor, event) => {
      const currentState = editorStateRef.current;
      const payload = getClipboardPayload();
      if (!currentState || !payload || !writeClipboardData(event, payload)) {
        event.preventDefault();
        return;
      }

      const nextContent = Modifier.removeRange(
        currentState.getCurrentContent(),
        currentState.getSelection(),
        'forward'
      );
      setEditorState(
        EditorState.push(currentState, nextContent, 'remove-range')
      );
    },
    [getClipboardPayload, setEditorState]
  );

  const handleClipboardPaste = useCallback(
    (_draftEditor, event) => {
      const currentState = editorStateRef.current;
      if (!currentState) {
        event.preventDefault();
        return;
      }

      const html = getClipboardData(event, 'text/html');
      const mimeData =
        getClipboardData(event, MIXED_TYPOGRAPHY_CLIPBOARD_MIME_TYPE) ||
        getMixedTypographyMimeDataFromHtml(html);
      const canDecodeMixedTypography =
        Boolean(mimeData) &&
        isMixedTypographyClipboardFromCurrentSession(mimeData);
      let decodedValue = null;
      if (canDecodeMixedTypography) {
        try {
          decodedValue = decodeMixedTypographyClipboard(mimeData, {
            currentFontReferenceMap: referenceRegistryRef.current,
            preserveCurrentSessionFonts: true,
            destinationDefaults: {
              font: formatterContextRef.current.defaultFont,
              fontSize: formatterContextRef.current.defaultFontSize,
              lineHeight: formatterContextRef.current.defaultLineHeight,
              fontWeight: formatterContextRef.current.defaultFontWeight,
              fontStyle: formatterContextRef.current.defaultFontStyle,
            },
          });
        } catch {
          // Clipboard input is untrusted; invalid payloads use the sanitized
          // standard HTML/text paste path below.
        }
      }
      if (decodedValue) {
        event.preventDefault();
        const nextReferenceMap = decodedValue.fontReferenceMap;
        if (
          hasUnresolvedOpaqueFontReferences(currentState) &&
          nextReferenceMap.size > 0 &&
          !isWholeContentSelected(currentState)
        ) {
          return;
        }
        const fragment = customImport(decodedValue.content, {
          ...formatterContextRef.current,
          fontReferenceMap: nextReferenceMap,
        });
        const nextContent = Modifier.replaceWithFragment(
          currentState.getCurrentContent(),
          currentState.getSelection(),
          fragment.getBlockMap()
        );
        referenceRegistryRef.current = nextReferenceMap;
        if (nextReferenceMap.size > 0 || isWholeContentSelected(currentState)) {
          opaqueFontReferencesRef.current = undefined;
        }
        setReferenceRegistryVersion((version) => version + 1);
        setEditorState(
          EditorState.push(currentState, nextContent, 'insert-fragment')
        );
        return;
      }

      event.preventDefault();
      handlePastedText(
        getClipboardData(event, 'text/plain'),
        html,
        currentState
      );
    },
    [handlePastedText, hasUnresolvedOpaqueFontReferences, setEditorState]
  );

  const clearState = useCallback(() => {
    editorStateRef.current = null;
    sessionKeyRef.current = null;
    referenceRegistryRef.current = new Map();
    opaqueFontReferencesRef.current = undefined;
    formatterContextRef.current = {};
    setFormatterContext({});
    setReferenceRegistryVersion((version) => version + 1);
    setEditorState(null);
  }, [setEditorState]);

  const hasCurrentEditor = Boolean(editorState);

  const selectionActions = useSelectionManipulation(
    editorState,
    setEditorState
  );

  const getFontReferenceIdForSelection = useCallback(
    (font) => {
      const currentState = editorStateRef.current;
      if (
        hasUnresolvedOpaqueFontReferences(currentState) &&
        !isWholeContentSelected(currentState)
      ) {
        return undefined;
      }

      if (
        !font ||
        areFontsEqual(font, formatterContextRef.current.defaultFont)
      ) {
        if (opaqueFontReferencesRef.current) {
          opaqueFontReferencesRef.current = undefined;
          setReferenceRegistryVersion((version) => version + 1);
        }
        return null;
      }

      const currentReferences = getReferencesFromMap(
        referenceRegistryRef.current
      );
      const { reference, fontReferences } = ensureTextFontReference(
        font,
        currentReferences
      );
      const nextReferenceRegistry = getTextFontReferenceMap(fontReferences);
      if (!nextReferenceRegistry.has(reference.id)) {
        return undefined;
      }
      referenceRegistryRef.current = nextReferenceRegistry;
      opaqueFontReferencesRef.current = undefined;
      setReferenceRegistryVersion((version) => version + 1);
      return reference.id;
    },
    [hasUnresolvedOpaqueFontReferences]
  );

  const setFontFamilyInSelection = useCallback(
    (font) => {
      const referenceId = getFontReferenceIdForSelection(font);
      if (typeof referenceId === 'undefined') {
        return;
      }
      selectionActions.setFontReferenceInSelection(referenceId);
    },
    [getFontReferenceIdForSelection, selectionActions]
  );

  const setFontSizeInSelection = useCallback(
    (fontSize) =>
      selectionActions.setFontSizeInSelection({
        fontSize,
        defaultFontSize: formatterContextRef.current.defaultFontSize,
      }),
    [selectionActions]
  );

  const setLineHeightInSelection = useCallback(
    (lineHeight) => {
      const currentState = editorStateRef.current;
      if (!currentState || getStateInfo(currentState).hasList) {
        return;
      }
      selectionActions.setLineHeightInSelection({
        lineHeight,
        defaultLineHeight: formatterContextRef.current.defaultLineHeight,
      });
    },
    [selectionActions]
  );

  const setFontWeightInSelection = useCallback(
    (fontWeight) =>
      selectionActions.setFontWeightInSelection(
        fontWeight,
        formatterContextRef.current.defaultFontWeight
      ),
    [selectionActions]
  );

  const toggleBoldInSelection = useCallback(
    (isBold) =>
      selectionActions.toggleBoldInSelection(
        isBold,
        formatterContextRef.current.defaultFontWeight
      ),
    [selectionActions]
  );

  const toggleItalicInSelection = useCallback(
    (isItalic) =>
      selectionActions.toggleItalicInSelection(
        isItalic,
        formatterContextRef.current.defaultFontStyle
      ),
    [selectionActions]
  );

  const applyTextStylePresetInSelection = useCallback(
    (preset = {}) => {
      let nextState = editorStateRef.current;
      if (!nextState || nextState.getSelection().isCollapsed()) {
        return;
      }
      const selectionHasList = getStateInfo(nextState).hasList;

      if (preset.font) {
        const referenceId = getFontReferenceIdForSelection(preset.font);
        if (typeof referenceId !== 'undefined') {
          nextState = fontFamilyFormatter.setters.setFontReference(
            nextState,
            referenceId
          );
        }
      }

      if (Number.isFinite(preset.fontSize)) {
        nextState = fontSizeFormatter.setters.setFontSize(nextState, {
          fontSize: preset.fontSize,
          defaultFontSize: formatterContextRef.current.defaultFontSize,
        });
      }
      if (!selectionHasList && Number.isFinite(preset.lineHeight)) {
        nextState = lineHeightFormatter.setters.setLineHeight(nextState, {
          lineHeight: preset.lineHeight,
          defaultLineHeight: formatterContextRef.current.defaultLineHeight,
        });
      }
      if (Number.isFinite(preset.fontWeight)) {
        nextState = weightFormatter.setters.setFontWeight(
          nextState,
          preset.fontWeight,
          formatterContextRef.current.defaultFontWeight
        );
      }
      if (typeof preset.isItalic === 'boolean') {
        nextState = italicFormatter.setters.toggleItalic(
          nextState,
          preset.isItalic,
          formatterContextRef.current.defaultFontStyle
        );
      }
      if (typeof preset.isUnderline === 'boolean') {
        nextState = underlineFormatter.setters.toggleUnderline(
          nextState,
          preset.isUnderline
        );
      }
      if (Number.isFinite(preset.letterSpacing)) {
        nextState = letterSpacingFormatter.setters.setLetterSpacing(
          nextState,
          preset.letterSpacing
        );
      }
      if (preset.color && typeof preset.color === 'object') {
        nextState = colorFormatter.setters.setColor(nextState, preset.color);
      }

      setEditorState(nextState);
    },
    [getFontReferenceIdForSelection, setEditorState]
  );

  /*
   * Keep this writer separate from the generic generated selection actions:
   * every preset formatter must compose from the previous Draft state so a
   * single click cannot lose earlier family/size/style updates to batching.
   */
  const selectionActionsWithTypography = useMemo(
    () => ({
      ...selectionActions,
      setFontFamilyInSelection,
      setFontSizeInSelection,
      setLineHeightInSelection,
      setFontWeightInSelection,
      toggleBoldInSelection,
      toggleItalicInSelection,
      applyTextStylePresetInSelection,
    }),
    [
      selectionActions,
      setFontFamilyInSelection,
      setFontSizeInSelection,
      setLineHeightInSelection,
      setFontWeightInSelection,
      toggleBoldInSelection,
      toggleItalicInSelection,
      applyTextStylePresetInSelection,
    ]
  );

  const getValueFromContent = useCallback((contentState) => {
    if (!contentState) {
      return null;
    }

    const context = {
      ...formatterContextRef.current,
      fontReferenceMap: referenceRegistryRef.current,
    };
    const content = customExport(
      EditorState.createWithContent(contentState),
      context
    );
    const currentReferences = pruneUnusedFontReferences(
      content,
      getReferencesFromMap(referenceRegistryRef.current)
    );

    return {
      content,
      fontReferences: opaqueFontReferencesRef.current || currentReferences,
    };
  }, []);

  const getValueFromState = useCallback(
    (state) => getValueFromContent(state?.getCurrentContent()),
    [getValueFromContent]
  );

  const value = {
    state: {
      editorState,
      hasCurrentEditor,
      selectionInfo,
      formatterContext: resolvedFormatterContext,
      referenceRegistryVersion,
    },
    actions: {
      setStateFromValue,
      updateEditorState,
      getHandleKeyCommand,
      handlePastedText,
      handleClipboardCopy,
      handleClipboardCut,
      handleClipboardPaste,
      clearState,
      selectionActions: selectionActionsWithTypography,
      getValueFromContent,
      getValueFromState,
    },
  };

  return (
    <RichTextContext.Provider value={value}>
      {children}
    </RichTextContext.Provider>
  );
}

RichTextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RichTextProvider;
